import {
  i18nStore,
  permissionsStore,
  routeStore,
  scopeStore,
  preferenceServiceStore,
  serverContextStore,
  tokenStore,
  userContextStore,
  siteMapStore,
  versionStore,
  settingsContextStore,
} from '@bingads-webui-campaign/global-context-store';
import { StateStore } from '@bingads-webui/state-store';
// @ts-ignore
import { isCompatibleBrowser } from '@bingads-webui-campaign/browser';
// @ts-ignore
import { isAdBlockOn } from '@bingads-webui-campaign/block-ad-block';
// @ts-ignore
import { getEntityFromScope, edmCampaign } from '@bingads-webui/edm-campaign';
import _ from 'underscore';

import { IScope, ScopeLevel } from '../../types/notification.types';
import { IRoute } from '../../types/route.types';
import { IContext } from '../../types/context.types';
import { Route } from '../route';

function getScopeWithHash(scope?: IScope) {
  if (!scope) {
    return undefined;
  }
  const { currentAccount, currentEntity } = getEntityFromScope(scope);
  const values = _.assign({}, scope.values);

  // smart campaign put campaign id in url hash part, so we need special handle it here
  const match = scope.location?.hash?.match(/\/campaign\/(\d+)/);
  if (currentAccount === currentEntity && match) {
    // eslint-disable-next-line prefer-destructuring
    values.campaignId = match[1];
  }

  return {
    values,
    location: scope.location,
  };
}

interface ContextState {
  refresh: number;
}

export class Context extends StateStore<ContextState> implements IContext {
  route: IRoute;
  lastScope?: IScope;
  scopeChangeLevel: ScopeLevel;
  isCompatibleBrowser: any;

  constructor() {
    const defaultState = {
      refresh: 0,
    };

    super(defaultState);
    this.route = new Route(this);
    this.lastScope = this.cloneScope(scopeStore.state.scope);
    this.scopeChangeLevel = ScopeLevel.root;
    this.isCompatibleBrowser = isCompatibleBrowser;

    scopeStore.subscribe(() => {
      this.onScopeChange();
    });

    versionStore.subscribe(() => this.refresh());
  }

  onScopeChange() {
    const clone = this.cloneScope(scopeStore.state.scope);
    const scope = getScopeWithHash(clone);
    if (this.isShouldRefresh(scope)) {
      this.calcScopeChangeLevel(scope);
      this.lastScope = scope;
      this.refresh();
    }
  }

  cloneScope(scope?: IScope): IScope | undefined {
    if (scope) {
      return {
        values: _.assign({}, scope.values),
        location: _.assign({}, scope.location),
      };
    }
    return undefined;
  }

  isShouldRefresh(scope?: IScope) {
    if (this.lastScope && scope) {
      if (
        scope.values.aid === this.lastScope.values.aid &&
        scope.values.cid === this.lastScope.values.cid &&
        scope.values.campaignId === this.lastScope.values.campaignId &&
        scope.values.adGroupId === this.lastScope.values.adGroupId
      ) {
        return false;
      }
      return true;
    }
    return true;
  }

  calcScopeChangeLevel(scope?: IScope) {
    if (this.lastScope && scope) {
      if (scope.values.cid !== this.lastScope.values.cid) {
        this.scopeChangeLevel = ScopeLevel.customer;
        return;
      }

      if (scope.values.aid !== this.lastScope.values.aid) {
        this.scopeChangeLevel = ScopeLevel.account;
        return;
      }

      if (scope.values.campaignId !== this.lastScope.values.campaignId) {
        this.scopeChangeLevel = ScopeLevel.campaign;
        return;
      }

      if (scope.values.adGroupId !== this.lastScope.values.adGroupId) {
        this.scopeChangeLevel = ScopeLevel.adGroup;
      }
    }
  }

  refresh() {
    this.setState({
      refresh: this.state.refresh + 1,
    });
  }

  getNewI18n() {
    return i18nStore.state.i18n;
  }

  getAuthToken() {
    return tokenStore.state.authToken;
  }

  getChangeScope() {
    return this.scopeChangeLevel;
  }

  /* istanbul ignore next */
  getCurrentScope(): IScope {
    return getScopeWithHash(scopeStore.state.scope) as IScope;
  }

  /* istanbul ignore next */
  getScopeObject(): IScope {
    return scopeStore.state.scope as IScope;
  }

  getCurrentPermissions() {
    return permissionsStore.state.permissions;
  }

  getServerContext() {
    return serverContextStore.state.serverContext;
  }

  /* istanbul ignore next */
  getPreferenceService() {
    return preferenceServiceStore.state.preferenceService;
  }

  getUserContext() {
    return userContextStore.state.userContext as any;
  }

  getRouteHistory() {
    return routeStore.state.history;
  }

  getRouteLocation() {
    return routeStore.state.location;
  }

  getEdmCampaign() {
    return edmCampaign;
  }

  /* istanbul ignore next */
  getSiteMap() {
    return siteMapStore.state.siteMap;
  }

  /* istanbul ignore next */
  getSettingsContext() {
    return settingsContextStore.state.settingsContext;
  }

  /* istanbul ignore next */
  isVersionRefreshed() {
    return versionStore.state.version !== versionStore.state.newVersion;
  }

  /* istanbul ignore next */
  async isAdBlockOn(): Promise<boolean> {
    return isAdBlockOn();
  }
}
